import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  Users, 
  MapPin, 
  Clock, 
  Calendar,
  Sparkles,
  Target,
  Lightbulb,
  Rocket,
  Star,
  Tag
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import CalEmbed from '../components/CalEmbed';
import { useLanguage } from '../context/LanguageContext';
import { CountdownTimer } from '../components/PriceDisplay';
import { usePricingSettings, formatDeadline, formatPrice, isDiscountActive } from '../hooks/useDiscountDeadline';

const WorkshopBookingPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { discount_deadline: discountDeadline, original_price: originalPrice, current_price: currentPrice } = usePricingSettings();
  const priceSavings = originalPrice - currentPrice;
  const discountPct = Math.round((priceSavings / originalPrice) * 100);
  const discountActive = isDiscountActive(discountDeadline);
  const [showCalendar, setShowCalendar] = useState(false);

  const workshopFeatures = [
    {
      icon: Target,
      title: language === 'nl' ? 'AI for HR Quickscan' : 'AI for HR Quickscan',
      description: language === 'nl' 
        ? 'Start met een grondige analyse van uw huidige AI-volwassenheid'
        : 'Start with a thorough analysis of your current AI for HR'
    },
    {
      icon: Lightbulb,
      title: language === 'nl' ? 'Use Case Identificatie' : 'Use Case Identification',
      description: language === 'nl'
        ? 'Ontdek de meest impactvolle AI-toepassingen voor uw HR-uitdagingen'
        : 'Discover the most impactful AI applications for your HR challenges'
    },
    {
      icon: Rocket,
      title: language === 'nl' ? 'Werkend Prototype' : 'Working Prototype',
      description: language === 'nl'
        ? 'Ga naar huis met een werkend AI-prototype dat u direct kunt testen'
        : 'Leave with a working AI prototype you can test immediately'
    },
    {
      icon: Sparkles,
      title: language === 'nl' ? 'AI Roadmap' : 'AI Roadmap',
      description: language === 'nl'
        ? 'Ontvang een gepersonaliseerde roadmap voor AI-implementatie'
        : 'Receive a personalized roadmap for AI implementation'
    }
  ];

  const agenda = [
    { 
      time: '09:00 - 09:30', 
      title: language === 'nl' ? 'Introductie & Quickscan Review' : 'Introduction & Quickscan Review',
      description: language === 'nl' ? 'Bespreken van uw AI-maturity score en focusgebieden' : 'Review of your AI for HR score and focus areas'
    },
    { 
      time: '09:30 - 10:30', 
      title: language === 'nl' ? 'Use Case Deep Dive' : 'Use Case Deep Dive',
      description: language === 'nl' ? 'Selectie en prioritering van 1-2 high-impact use cases' : 'Selection and prioritization of 1-2 high-impact use cases'
    },
    { 
      time: '10:30 - 10:45', 
      title: language === 'nl' ? 'Pauze' : 'Break',
      description: language === 'nl' ? 'Koffie en netwerken' : 'Coffee and networking'
    },
    { 
      time: '10:45 - 12:00', 
      title: language === 'nl' ? 'Hands-on Prototype Building' : 'Hands-on Prototype Building',
      description: language === 'nl' ? 'Samen een werkend AI-prototype bouwen met no-code tools' : 'Build a working AI prototype together using no-code tools'
    },
    { 
      time: '12:00 - 12:30', 
      title: language === 'nl' ? 'Roadmap & Volgende Stappen' : 'Roadmap & Next Steps',
      description: language === 'nl' ? 'Actieplan en implementatie-roadmap opstellen' : 'Create action plan and implementation roadmap'
    },
  ];

  const testimonials = [
    {
      quote: language === 'nl' 
        ? "De workshop gaf ons direct inzicht in waar we moesten beginnen. We implementeerden CV-screening in 2 weken."
        : "The workshop gave us immediate clarity on where to start. We implemented CV screening in 2 weeks.",
      author: "Sarah De Vries",
      role: "HR Director, TechCorp Belgium"
    },
    {
      quote: language === 'nl'
        ? "Eindelijk een no-nonsense aanpak van AI in HR. De benchmark data opende onze ogen."
        : "Finally, a no-nonsense approach to AI in HR. The benchmark data opened our eyes.",
      author: "Marc Janssens",
      role: "CHRO, FinanceGroup"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-blue-50">
      <Header />
      
      <main className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {language === 'nl' ? 'Terug' : 'Back'}
          </button>

          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Sparkles className="h-4 w-4" />
              {language === 'nl' ? 'Meest geboekte workshop' : 'Most booked workshop'}
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              AI for HR Roadmap Workshop
            </h1>
            
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
              {language === 'nl' 
                ? 'Van quickscan naar werkend prototype in één halve dag. Ontdek hoe AI jouw HR-team kan transformeren.'
                : 'From quickscan to working prototype in half a day. Discover how AI can transform your HR team.'}
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <Clock className="h-5 w-5 text-blue-500" />
                <span className="font-medium">4 {language === 'nl' ? 'uur' : 'hours'}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <Users className="h-5 w-5 text-blue-500" />
                <span className="font-medium">5-15 {language === 'nl' ? 'deelnemers' : 'participants'}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <MapPin className="h-5 w-5 text-green-500" />
                <span className="font-medium">{language === 'nl' ? 'Op locatie of online' : 'On-site or online'}</span>
              </div>
            </div>

            {/* Price with anchoring and urgency */}
            <div className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-2xl shadow-lg">
              {discountActive ? (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="bg-white/20 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {language === 'nl' ? 'INTRODUCTIEPRIJS' : 'INTRODUCTORY PRICE'}
                    </span>
                    <span className="text-blue-200 text-xs">-{discountPct}%</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-blue-200 line-through text-lg">{formatPrice(originalPrice, language)}</span>
                    <span className="text-3xl font-bold">{formatPrice(currentPrice, language)}</span>
                    <span className="text-blue-200 text-sm font-normal">{language === 'nl' ? 'excl. BTW' : 'excl. VAT'}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-blue-100 text-sm">
                    <Tag className="h-4 w-4" />
                    <span>{language === 'nl' ? `Bespaar ${formatPrice(priceSavings, 'nl')}` : `Save ${formatPrice(priceSavings, 'en')}`}</span>
                    <span className="mx-1">•</span>
                    <span>{language === 'nl' ? `t/m ${formatDeadline(discountDeadline, 'nl')}` : `until ${formatDeadline(discountDeadline, 'en')}`}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-white/20">
                    <CountdownTimer targetDate={new Date(`${discountDeadline}T23:59:59`)} subtle darkBg />
                  </div>
                </>
              ) : (
                <div className="flex items-baseline gap-3" data-testid="workshop-price-normal">
                  <span className="text-3xl font-bold">{formatPrice(originalPrice, language)}</span>
                  <span className="text-blue-200 text-sm font-normal">{language === 'nl' ? 'excl. BTW' : 'excl. VAT'}</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            
            {/* Left Column - Workshop Info */}
            <div className="space-y-6">
              
              {/* What You'll Get */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  {language === 'nl' ? 'Wat je krijgt' : 'What you\'ll get'}
                </h2>
                <div className="space-y-4">
                  {workshopFeatures.map((feature, index) => (
                    <div key={feature.title || `feat-${index}`} className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <feature.icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Agenda */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  Agenda
                </h2>
                <div className="space-y-3">
                  {agenda.map((item, index) => (
                    <div key={item.time || `slot-${index}`} className="flex gap-4 pb-3 border-b border-gray-100 last:border-0">
                      <div className="text-blue-500 font-mono text-sm font-medium w-28 flex-shrink-0">
                        {item.time}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{item.title}</h3>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Testimonials */}
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-6 text-white">
                <h2 className="text-xl font-bold mb-4">
                  {language === 'nl' ? 'Wat deelnemers zeggen' : 'What participants say'}
                </h2>
                <div className="space-y-4">
                  {testimonials.map((testimonial, index) => (
                    <div key={testimonial.quote || `tst-${index}`} className="bg-white/10 rounded-lg p-4">
                      <p className="text-white/90 italic mb-2">"{testimonial.quote}"</p>
                      <div className="text-sm">
                        <span className="font-semibold">{testimonial.author}</span>
                        <span className="text-white/70"> • {testimonial.role}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Booking */}
            <div className="lg:sticky lg:top-4 h-fit">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white text-center">
                  <h2 className="text-xl font-bold">
                    {language === 'nl' ? 'Boek je workshop' : 'Book your workshop'}
                  </h2>
                  <p className="text-blue-100 text-sm">
                    {language === 'nl' ? 'Selecteer een datum die je past' : 'Select a date that works for you'}
                  </p>
                </div>
                
                {/* Cal.com Embed */}
                <div className="p-0">
                  <CalEmbed 
                    calLink="humanistix"
                    calDomain="app.cal.eu"
                    style={{ minHeight: '600px' }}
                  />
                </div>
              </div>

              {/* Alternative Contact */}
              <div className="mt-4 text-center text-gray-500 text-sm">
                {language === 'nl' ? 'Liever eerst een gesprek?' : 'Prefer a call first?'}
                <br />
                <a 
                  href="mailto:dominique@humanistix.be" 
                  className="text-blue-500 hover:underline font-medium"
                >
                  dominique@humanistix.be
                </a>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-2">
              {language === 'nl' ? 'Nog vragen over de workshop?' : 'Still have questions about the workshop?'}
            </h2>
            <p className="text-slate-300 mb-6">
              {language === 'nl' 
                ? 'Boek een gratis 15-minuten call om te bespreken of deze workshop past bij jouw organisatie'
                : 'Book a free 15-minute call to discuss if this workshop fits your organization'}
            </p>
            <Button 
              onClick={() => window.open('https://app.cal.eu/humanistix', '_blank')}
              className="bg-blue-500 hover:bg-blue-600 px-8 py-3"
            >
              <Calendar className="h-4 w-4 mr-2" />
              {language === 'nl' ? 'Plan een gesprek' : 'Schedule a call'}
            </Button>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default WorkshopBookingPage;
